import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { DocumentViewer } from 'components/documents-search';
import { HorizontalDraggableDivider } from 'components/draggable-divider';
import type { RowData } from './types';
import { previewName, previewSubtitle } from './utils';
import styles from './results-section.module.scss';

const MIN_WIDTH = 360;
const MAX_WIDTH = 1000;

interface PreviewRailProps {
  // The page's preview host, once found; the rail portals into it.
  host: HTMLElement | null;
  // The document in the rail; stays set through the slide-out.
  documentId?: number;
  // False while the rail slides out after closing.
  open: boolean;
  row?: RowData;
  onPrev: () => void;
  onNext: () => void;
  hasPrev: boolean;
  hasNext: boolean;
  onClose: () => void;
}

/**
 * The side-by-side document image preview (mirrors the /documents page): a
 * full-height rail portaled into the page's preview host (so it spans the map
 * and results, not just this section), resizable by dragging its left edge.
 * @param {PreviewRailProps} props The rail.
 * @return {JSX.Element | null} The rail.
 */
export function PreviewRail({
  host,
  documentId,
  open,
  row,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
  onClose,
}: PreviewRailProps) {
  const [width, setWidth] = useState(640);

  // Publish the open rail's width as a CSS var on the shared preview host, so
  // the map controls and the results toolbar (both descendants) shift left by it
  // and stay clear of the overlay. Tracks resize; resets to 0 when closed.
  useEffect(() => {
    host?.style.setProperty('--mq-preview-offset', open ? `${width}px` : '0px');
    // Reset on unmount so hiding the results table doesn't leave the map
    // controls shifted in by a stale offset until a results section mounts again.
    return () => host?.style.setProperty('--mq-preview-offset', '0px');
  }, [host, open, width]);

  if (!host || documentId === undefined) return null;
  return createPortal(
    <div
      className={classNames(styles.viewerPanel, {
        [styles.viewerPanelClosing]: !open,
      })}
      style={{ width }}
    >
      <HorizontalDraggableDivider
        side="right"
        minWidth={MIN_WIDTH}
        maxWidth={MAX_WIDTH}
        value={width}
        onDrag={setWidth}
        label="Resize preview"
      />
      <div className={styles.viewerBody}>
        <DocumentViewer
          documentId={documentId}
          documentName={previewName(row)}
          subtitle={previewSubtitle(row)}
          hideToolbar
          onPrev={onPrev}
          onNext={onNext}
          hasPrev={hasPrev}
          hasNext={hasNext}
          onClose={onClose}
        />
      </div>
    </div>,
    host
  );
}
